import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import AppConstants from "@src/utils/app.constants";
import {
  DocumentStore,
  NewDocumentModelRef,
  NewDocumentPayload,
} from "../domain/documents.model";
import api from "@src/common/services/api.service";
import { ApplicationResponse } from "@src/common/domain/common.model";

export const uploadDocument = createAsyncThunk<
  string,
  { payload: NewDocumentPayload }
>("document/upload", async ({ payload }) => {
  const url = `${AppConstants.api}/document/upload`;
  const response = await api.post<ApplicationResponse<string>>(url, payload, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data.data || "";
});

export const deleteDocument = createAsyncThunk<
  any,
  { id: string; userId: string }
>("document/delete", async ({ id, userId }) => {
  const url = `${AppConstants.api}/document/delete`;
  const response = await api.delete<ApplicationResponse<void>>(url, {
    params: {
      id,
      "user-id": userId,
    },
  });
  return response.data;
});

export const downloadDocument = createAsyncThunk<void, any>(
  "document/download",
  async (rowdata) => {
    const url = `${AppConstants.api}/document/download`;
    const response = await api.get(url, {
      params: { id: rowdata.id },
      responseType: "blob",
    });

    const contentDisposition = response.headers["content-disposition"];
    let fileName = "document";
    if (contentDisposition) {
      const matches = contentDisposition.match(/filename="(.+)"/);
      if (matches && matches[1]) {
        fileName = matches[1];
      }
    }

    // Create a blob from the response data and download it
    const blob = new Blob([response.data], { type: response.data.type });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
);

export const fetchDocumentList = createAsyncThunk<
  any[],
  { projectId: string; projectType: "npms" | "eci" | "custom" }
>("projectList/fetchUsersProjectsList", async ({ projectType, projectId }) => {
  const type = projectType.toUpperCase();
  const url = `${AppConstants.api}/document/list/${type}/${projectId}`;
  const response = await api.get<ApplicationResponse<any[]>>(url);
  return response.data.data || [];
});

const DocumentSlice = createSlice({
  name: "documents",
  initialState: {
    minimal: { status: "loading", error: null, data: [] },
  } as DocumentStore,
  reducers: {
    resetDocuments: (state) => {
      state.minimal = { status: "loading", error: null, data: [] };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDocumentList.pending, (state, action) => {
        state.minimal = {
          status: "loading",
          data: [],
          error: null,
        };
      })
      .addCase(fetchDocumentList.fulfilled, (state, action) => {
        state.minimal = {
          status: "idle",
          data: action.payload,
          error: null,
        };
      })
      .addCase(fetchDocumentList.rejected, (state, action) => {
        state.minimal = {
          status: "failed",
          data: [],
          error: action.error,
        };
      });
  },
});

export const { resetDocuments } = DocumentSlice.actions;
export default DocumentSlice.reducer;
